import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Mail, Building2, Clock, Check, ArrowUpRight, Filter, Search, ChevronDown, Flame, Zap, Snowflake, TrendingUp, RefreshCw, ArrowDownUp, Info, Eye, BarChart3, Play, Timer, XCircle, Send, Bell, Trash2 } from 'lucide-react';
import { DashboardLayout } from '../components/DashboardLayout';
import PageTour from '@/components/PageTour';
import api from '@/lib/api';

const heading = { fontFamily: "'Outfit', sans-serif", color: '#1D1D1F' };

const STATUS_CONFIG = {
  new: { bg: 'bg-info/10', text: 'text-info', dot: 'bg-info', label: 'New' },
  contacted: { bg: 'bg-warning/10', text: 'text-warning', dot: 'bg-warning', label: 'Contacted' },
  qualified: { bg: 'bg-purple-50', text: 'text-purple-600', dot: 'bg-purple-500', label: 'Qualified' },
  converted: { bg: 'bg-success/10', text: 'text-success', dot: 'bg-success', label: 'Converted' },
  lost: { bg: 'bg-gray-50', text: 'text-gray-500', dot: 'bg-gray-400', label: 'Lost' },
};

const GRADE_CONFIG = {
  hot: { icon: Flame, bg: 'bg-error/10', text: 'text-error', border: 'border-error/30', label: 'Hot' },
  warm: { icon: Zap, bg: 'bg-warning/10', text: 'text-warning', border: 'border-warning/30', label: 'Warm' },
  cold: { icon: Snowflake, bg: 'bg-info/10', text: 'text-info', border: 'border-info/30', label: 'Cold' },
};

export default function LeadsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [rescoring, setRescoring] = useState(false);
  const [expandedScore, setExpandedScore] = useState(null);
  const [nurture, setNurture] = useState(null);
  const [showNurture, setShowNurture] = useState(false);
  const [processing, setProcessing] = useState(false);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const [leadRes, nurtureRes] = await Promise.all([
        api.get('/leads'),
        api.get('/leads/nurture'),
      ]);
      setData(leadRes.data);
      setNurture(nurtureRes.data);
    } catch {
      setData(null);
    }
    setLoading(false);
  };

  useEffect(() => { fetchLeads(); }, []);

  const updateStatus = async (email, status) => {
    try {
      await api.patch(`/leads/${encodeURIComponent(email)}/status`, { status });
      fetchLeads();
    } catch { /* ignore */ }
  };

  const handleRescore = async () => {
    setRescoring(true);
    try {
      await api.post('/leads/rescore');
      fetchLeads();
    } catch { /* ignore */ }
    setRescoring(false);
  };

  const handleProcessNurture = async () => {
    setProcessing(true);
    try {
      await api.post('/leads/nurture/process');
      fetchLeads();
    } catch { /* ignore */ }
    setProcessing(false);
  };

  const handleCancelNurture = async (email) => {
    try {
      await api.post(`/leads/nurture/${encodeURIComponent(email)}/cancel`);
      fetchLeads();
    } catch { /* ignore */ }
  };

  const handleDeleteLead = async (email) => {
    if (!window.confirm(`Delete lead "${email}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/leads/${encodeURIComponent(email)}`);
      fetchLeads();
    } catch { /* ignore */ }
  };

  const leads = (data?.leads || [])
    .filter(l => {
      const matchSearch = !search || l.name?.toLowerCase().includes(search.toLowerCase()) || l.email?.toLowerCase().includes(search.toLowerCase());
      const matchStatus = filterStatus === 'all' || l.status === filterStatus;
      return matchSearch && matchStatus;
    })
    .sort((a, b) => {
      if (sortBy === 'score') return (b.score || 0) - (a.score || 0);
      return 0; // default: already sorted by date from backend
    });

  const stats = data?.stats || {};

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="leads-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-[#1D1D1F]" style={heading}>Lead Pipeline</h1>
            <p className="text-[13px] text-[#86868B] mt-1">Manage and score leads from your landing page</p>
          </div>
          <div className="flex items-center gap-3">
            <PageTour storageKey="tour_leads" steps={[
              { target: '[data-testid="toggle-nurture-btn"]', title: 'Lead Nurturing', content: 'Automated follow-up sequences to engage and convert leads over time.' },
              { target: '[data-testid="rescore-btn"]', title: 'AI Scoring', content: 'AI re-scores all leads based on engagement, demographics, and behavior patterns.' },
              { target: '[data-testid="leads-table"]', title: 'Lead Pipeline', content: 'View all leads with scores, status, and contact details. Filter by temperature (hot/warm/cold).' },
            ]} />
            <button onClick={() => setShowNurture(!showNurture)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-[12px] font-semibold transition-colors ${
                showNurture ? 'bg-orange-500 text-white' : 'bg-orange-50 text-orange-600 hover:bg-orange-100'
              }`}
              data-testid="toggle-nurture-btn-2">
              <Timer className="w-3.5 h-3.5" /> Nurture
              {nurture?.stats?.pending_steps > 0 && (
                <span className="w-5 h-5 rounded-full bg-white/20 text-[9px] font-bold flex items-center justify-center">
                  {nurture.stats.pending_steps}
                </span>
              )}
            </button>
            <button onClick={handleRescore} disabled={rescoring}
              className="flex items-center gap-1.5 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl text-[12px] font-semibold text-[#1D1D1F] transition-colors disabled:opacity-50"
              data-testid="rescore-btn-2">
              <RefreshCw className={`w-3.5 h-3.5 ${rescoring ? 'animate-spin' : ''}`} /> {rescoring ? 'Rescoring...' : 'Rescore All'}
            </button>
            <button onClick={fetchLeads} className="text-[13px] font-medium text-[#86868B] hover:text-[#1D1D1F] transition-colors" data-testid="leads-refresh-btn">
              Refresh
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {[
            { label: 'Total Leads', val: stats.total || 0, icon: Users, color: 'from-blue-500 to-indigo-600' },
            { label: 'Hot Leads', val: stats.hot_leads || 0, icon: Flame, color: 'from-red-500 to-orange-500' },
            { label: 'Avg Score', val: stats.avg_score || 0, icon: TrendingUp, color: 'from-violet-500 to-purple-600' },
            { label: 'Qualified', val: stats.qualified || 0, icon: Check, color: 'from-emerald-500 to-teal-600' },
            { label: 'Top Industry', val: stats.top_industries?.[0]?.[0] || '—', icon: Building2, color: 'from-orange-500 to-amber-500', isText: true },
          ].map((s, i) => (
            <motion.div key={s.label}
              initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm"
              data-testid={`leads-stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${s.color} flex items-center justify-center`}>
                  <s.icon className="w-4 h-4 text-white" />
                </div>
                <span className="text-[11px] font-semibold text-[#86868B] uppercase tracking-wider">{s.label}</span>
              </div>
              <p className={`${s.isText ? 'text-[15px]' : 'text-2xl'} font-bold text-[#1D1D1F]`} style={heading}>{s.val}</p>
            </motion.div>
          ))}
        </div>

        {/* Nurture Panel */}
        <AnimatePresence>
          {showNurture && nurture && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-8 overflow-hidden"
              data-testid="nurture-panel">
              <div className="bg-white rounded-xl border border-orange-100 shadow-sm p-6">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <Timer className="w-5 h-5 text-orange-500" />
                    <div>
                      <h2 className="text-[15px] font-semibold text-[#1D1D1F]" style={heading}>Nurture Sequences</h2>
                      <p className="text-[11px] text-[#86868B]">Automated follow-up reminders for your leads</p>
                    </div>
                  </div>
                  <button onClick={handleProcessNurture} disabled={processing}
                    className="flex items-center gap-1.5 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-[12px] font-semibold transition-colors disabled:opacity-50"
                    data-testid="process-nurture-btn">
                    <Send className={`w-3.5 h-3.5 ${processing ? 'animate-pulse' : ''}`} />
                    {processing ? 'Processing...' : `Process Due (${nurture.stats?.overdue || 0})`}
                  </button>
                </div>

                {/* Nurture Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-5">
                  {[
                    { label: 'Active Sequences', val: nurture.stats?.total_sequences || 0, color: 'text-info' },
                    { label: 'Pending Steps', val: nurture.stats?.pending_steps || 0, color: 'text-warning' },
                    { label: 'Sent', val: nurture.stats?.sent_steps || 0, color: 'text-success' },
                    { label: 'Cancelled', val: nurture.stats?.cancelled_steps || 0, color: 'text-gray-500' },
                  ].map(s => (
                    <div key={s.label} className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className={`text-xl font-bold ${s.color}`} style={heading}>{s.val}</p>
                      <p className="text-[10px] text-[#86868B] font-medium uppercase tracking-wider">{s.label}</p>
                    </div>
                  ))}
                </div>

                {/* Nurture Lead List */}
                {(nurture.leads || []).length > 0 ? (
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {nurture.leads.map((nl, i) => {
                      const g = GRADE_CONFIG[nl.grade] || GRADE_CONFIG.cold;
                      const GIcon = g.icon;
                      const pendingSteps = nl.steps.filter(s => s.status === 'pending');
                      const sentSteps = nl.steps.filter(s => s.status === 'sent');
                      return (
                        <div key={nl.email} className="flex items-center gap-4 bg-gray-50 rounded-lg px-4 py-3"
                          data-testid={`nurture-lead-${i}`}>
                          <div className={`w-8 h-8 rounded-lg ${g.bg} flex items-center justify-center shrink-0`}>
                            <GIcon className={`w-4 h-4 ${g.text}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-semibold text-[#1D1D1F] truncate">{nl.name}</p>
                            <p className="text-[10px] text-[#86868B] truncate">{nl.email} — Score {nl.score}</p>
                          </div>
                          {/* Step indicators */}
                          <div className="flex items-center gap-1">
                            {nl.steps.map((step, j) => (
                              <div key={j}
                                title={`${step.title} (${step.status})`}
                                className={`w-2 h-2 rounded-full ${
                                  step.status === 'sent' ? 'bg-success' :
                                  step.status === 'cancelled' ? 'bg-gray-300' :
                                  'bg-amber-400 animate-pulse'
                                }`} />
                            ))}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-[10px] text-[#86868B]">
                              {sentSteps.length}/{nl.steps.length} sent
                            </span>
                            {pendingSteps.length > 0 && (
                              <button onClick={() => handleCancelNurture(nl.email)}
                                className="text-[10px] text-error hover:text-error font-medium transition-colors"
                                data-testid={`cancel-nurture-${i}`}>
                                Cancel
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-[13px] text-[#86868B] text-center py-4">No nurture sequences yet. New leads will auto-enroll.</p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Filters + Sort */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
          <div className="relative flex-1 max-w-xs">
            <Search className="w-4 h-4 text-[#86868B] absolute left-3 top-1/2 -translate-y-1/2" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search leads..."
              className="w-full h-10 pl-10 pr-4 rounded-xl border border-gray-200 text-[13px] text-[#1D1D1F] focus:border-orange-300 focus:ring-1 focus:ring-orange-200 outline-none"
              data-testid="leads-search" />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-4 h-4 text-[#86868B]" />
            {['all', 'new', 'contacted', 'qualified', 'converted', 'lost'].map(s => (
              <button key={s} onClick={() => setFilterStatus(s)}
                className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all ${
                  filterStatus === s ? 'bg-[#1D1D1F] text-white' : 'bg-gray-100 text-[#86868B] hover:bg-gray-200'
                }`}
                data-testid={`leads-filter-${s}`}>
                {s === 'all' ? 'All' : STATUS_CONFIG[s]?.label || s}
              </button>
            ))}
            <div className="w-px h-6 bg-gray-200 mx-1 hidden sm:block" />
            <button onClick={() => setSortBy(sortBy === 'date' ? 'score' : 'date')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all ${
                sortBy === 'score' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-[#86868B] hover:bg-gray-200'
              }`}
              data-testid="sort-by-score-btn">
              <ArrowDownUp className="w-3 h-3" /> {sortBy === 'score' ? 'By Score' : 'By Date'}
            </button>
          </div>
        </div>

        {/* Leads Table */}
        {loading ? (
          <div className="text-center py-16 text-[#86868B]">Loading...</div>
        ) : leads.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="bg-white rounded-xl border border-gray-100 p-12 text-center shadow-sm" data-testid="leads-empty">
            <Users className="w-12 h-12 text-gray-200 mx-auto mb-3" />
            <p className="text-[15px] font-semibold text-[#1D1D1F]" style={heading}>No Leads Yet</p>
            <p className="text-[13px] text-[#86868B] mt-1">Leads from your landing page form will appear here.</p>
          </motion.div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" data-testid="leads-table-2">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#FAFAFA] text-[11px] font-semibold text-[#86868B] uppercase tracking-wider">
                    <th className="text-left px-5 py-3">Name</th>
                    <th className="text-left px-5 py-3">Email</th>
                    <th className="text-left px-5 py-3">Phone</th>
                    <th className="text-left px-5 py-3">Score</th>
                    <th className="text-left px-5 py-3">Engagement</th>
                    <th className="text-left px-5 py-3 hidden md:table-cell">Industry</th>
                    <th className="text-left px-5 py-3">Status</th>
                    <th className="text-left px-5 py-3 hidden sm:table-cell">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead, i) => {
                    const sc = STATUS_CONFIG[lead.status] || STATUS_CONFIG.new;
                    const grade = GRADE_CONFIG[lead.score_grade] || GRADE_CONFIG.cold;
                    const GradeIcon = grade.icon;
                    const isExpanded = expandedScore === lead.email;
  
                    return (
                      <motion.tr key={lead.email + i}
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}
                        className="border-t border-gray-50 hover:bg-[#FAFAFA] transition-colors"
                        data-testid={`lead-row-${i}`}>
                        <td className="px-5 py-3.5">
                          <p className="text-[13px] font-semibold text-[#1D1D1F]">{lead.name}</p>
                        </td>
                        <td className="px-5 py-3.5">
                          <a href={`mailto:${lead.email}`} className="text-[13px] text-info hover:underline flex items-center gap-1">
                            {lead.email} <ArrowUpRight className="w-3 h-3" />
                          </a>
                        </td>
                        <td className="px-5 py-3.5">
                          {lead.phone ? (
                            <a href={`tel:${lead.phone}`} className="text-[13px] text-muted-foreground hover:text-info transition-colors">
                              {lead.phone}
                            </a>
                          ) : (
                            <span className="text-[11px] text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="relative">
                            <button
                              onClick={() => setExpandedScore(isExpanded ? null : lead.email)}
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold border ${grade.bg} ${grade.text} ${grade.border} transition-all hover:opacity-80`}
                              data-testid={`lead-score-${i}`}>
                              <GradeIcon className="w-3 h-3" />
                              {lead.score || 0}
                              <Info className="w-2.5 h-2.5 opacity-50" />
                            </button>
                            <AnimatePresence>
                              {isExpanded && lead.score_breakdown && (
                                <motion.div
                                  initial={{ opacity: 0, y: -5, scale: 0.95 }}
                                  animate={{ opacity: 1, y: 0, scale: 1 }}
                                  exit={{ opacity: 0, y: -5, scale: 0.95 }}
                                  className="absolute top-full left-0 mt-1 z-20 bg-white rounded-xl border border-gray-100 shadow-xl p-3 min-w-[220px]"
                                  data-testid={`lead-score-breakdown-${i}`}>
                                  <p className="text-[10px] font-bold text-[#86868B] uppercase tracking-wider mb-2">Score Breakdown</p>
                                  {lead.score_breakdown.map((b, j) => (
                                    <div key={j} className="flex items-center justify-between py-1 text-[11px]">
                                      <span className="text-[#1D1D1F]">{b.signal}</span>
                                      <span className="text-success font-bold">+{b.points}</span>
                                    </div>
                                  ))}
                                  <div className="flex items-center justify-between pt-2 mt-2 border-t border-gray-100 text-[12px]">
                                    <span className="font-semibold text-[#1D1D1F]">Total</span>
                                    <span className="font-bold text-[#1D1D1F]">{lead.score || 0}</span>
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        </td>
                        {/* Engagement Column */}
                        <td className="px-5 py-3.5" data-testid={`lead-engagement-${i}`}>
                          {lead.engagement?.has_engagement ? (
                            <div className="flex items-center gap-2">
                              {lead.engagement.demo_completed ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-success/10 border border-success/30 text-success rounded-lg text-[10px] font-bold">
                                  <Check className="w-3 h-3" /> Full Demo
                                </span>
                              ) : lead.engagement.demo_started ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-info/10 border border-info/30 text-info rounded-lg text-[10px] font-bold">
                                  <Play className="w-3 h-3" /> {lead.engagement.steps_viewed}/{lead.engagement.steps_total}
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-50 border border-gray-200 text-gray-500 rounded-lg text-[10px] font-bold">
                                  <Eye className="w-3 h-3" /> Visited
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-[11px] text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-5 py-3.5 hidden md:table-cell">
                          <span className="text-[12px] text-[#86868B]">{lead.industry || '—'}</span>
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="relative group">
                            <button className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold ${sc.bg} ${sc.text}`}
                              data-testid={`lead-status-${i}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                              {sc.label}
                              <ChevronDown className="w-3 h-3" />
                            </button>
                            <div className="absolute top-full left-0 mt-1 bg-white rounded-lg border border-gray-100 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 min-w-[120px]"
                              data-testid={`lead-status-dropdown-${i}`}>
                              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                                <button key={key} onClick={() => updateStatus(lead.email, key)}
                                  className="w-full text-left px-3 py-2 text-[11px] font-medium hover:bg-gray-50 flex items-center gap-2 transition-colors"
                                  data-testid={`lead-status-option-${key}-${i}`}>
                                  <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                                  {cfg.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3.5 hidden sm:table-cell">
                          <span className="text-[11px] text-[#86868B]">
                            {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : '—'}
                          </span>
                        </td>
                        <td className="px-3 py-3.5">
                          <button
                            onClick={() => handleDeleteLead(lead.email)}
                            title="Delete lead"
                            data-testid={`lead-delete-${i}`}
                            className="p-1.5 rounded-md text-gray-400 hover:bg-error/10 hover:text-error transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
